/* eslint-disable no-console */
import { useCallback, useMemo } from 'react';
import { PROJECT_ID_PLACEHOLDER, PROJECT_ID_PREFIX } from '../constants';
import { SubmitOptions, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useModuleProjectContext } from 'common/contexts/useModuleProjectContext';

interface SanatizeUrl {
  url: string;
  urlProjectId: string;
  activeProjectId: string;
}

export const sanatizeNavigationUrl = ({
  url,
  urlProjectId,
  activeProjectId,
}: SanatizeUrl): string => {
  const projectId = urlProjectId.includes(PROJECT_ID_PREFIX)
    ? urlProjectId
    : `${PROJECT_ID_PREFIX}${activeProjectId}`;

  if (url.includes(PROJECT_ID_PLACEHOLDER)) {
    return url.replace(PROJECT_ID_PLACEHOLDER, projectId);
  }

  return url;
};

export const useNavigateWithProjectId = () => {
  const navigate = useNavigate();
  const { projectId: urlProjectId } = useParams();
  const { activeProject } = useModuleProjectContext();

  return useMemo(
    () => (url: string, options?: SubmitOptions) => {
      const newUrl = sanatizeNavigationUrl({
        url,
        urlProjectId: urlProjectId ?? '',
        activeProjectId: activeProject?.moduleProjectID ?? '',
      });

      navigate(newUrl, options);
    },
    [navigate, urlProjectId, activeProject]
  );
};

interface CompareUrlOptions {
  includes?: boolean;
  startsWith?: boolean;
  endsWith?: boolean;
  log?: boolean;
}

export function useCompareUrlWithProjectId() {
  const { pathname } = useLocation();
  const { projectId } = useParams();

  const compareUrl = useCallback(
    (urls: string | string[], options?: CompareUrlOptions): boolean => {
      const { includes, startsWith, endsWith, log } = options ?? {};
      const plainUrl = pathname.replace(projectId as string, PROJECT_ID_PLACEHOLDER);
      const urlArray = Array.isArray(urls) ? urls : [urls];

      return urlArray.some((url) => {
        if (typeof url !== 'string') {
          return false;
        }

        if (includes) {
          if (log) {
            console.log(`Plain URL: ${plainUrl}, URL: ${url}, Includes: ${plainUrl.includes(url)}`);
          }
          return plainUrl.includes(url);
        }

        if (startsWith) {
          if (log) {
            console.log(
              `Plain URL: ${plainUrl}, URL: ${url}, Starts With: ${plainUrl.startsWith(url)}`
            );
          }
          return plainUrl.startsWith(url);
        }

        if (endsWith) {
          if (log) {
            console.log(
              `Plain URL: ${plainUrl}, URL: ${url}, Ends With: ${plainUrl.endsWith(url)}`
            );
          }
          return plainUrl.endsWith(url);
        }

        if (log) {
          console.log(`Plain URL: ${plainUrl}, URL: ${url}, Equals: ${url === plainUrl}`);
        }
        return url === plainUrl;
      });
    },
    [pathname, projectId]
  );

  return useMemo(() => ({ compareUrl }), [compareUrl]);
}

export function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}
